/**
 * Unified log record used by both live (SSE) and historical (ClickHouse)
 * modes of the LogsViewer. The live path synthesizes fields from k8s pod
 * output; the historical path maps them from the ClickHouse LogEntry shape.
 */
export interface LogRecord {
  /** ISO 8601 timestamp. */
  ts: string;
  /** Uppercased level label. INFO is used as a fallback when unknown. */
  level: string;
  /** Message body (may contain ANSI escape sequences). */
  msg: string;
  /** Pod name (short form is computed at render time). */
  pod: string;
  /** Container name (nullable for historical rows without it). */
  container: string | null;
  /** Namespace (only populated in historical mode). */
  namespace?: string;
  /** Node the pod was running on (only populated in historical mode). */
  node?: string;
  /** Java-style logger class name (only populated in historical mode). */
  logger?: string;
  /** Correlation/trace ID for cross-service drilldown. */
  correlationId?: string;
  /** Structured context fields attached to the log entry. */
  context?: Record<string, string>;
  /** Stable-ish ID used as virtual-list key. */
  id: string;
}

export type LogLevel =
  | "TRACE"
  | "DEBUG"
  | "INFO"
  | "WARN"
  | "ERROR"
  | "FATAL"
  | (string & {});

export type LogsMode = "live" | "history";

export const LOG_LEVELS: LogLevel[] = ["ERROR", "WARN", "INFO", "DEBUG"];

export interface LogsUrlState {
  mode: LogsMode;
  pod: string;
  container: string;
  levels: string[];
  q: string;
  range: string; // "15m" | "1h" | "6h" | "24h" | "7d"
  cid: string;
}

export const DEFAULT_URL_STATE: LogsUrlState = {
  mode: "live",
  pod: "",
  container: "",
  levels: [],
  q: "",
  range: "1h",
  cid: "",
};

export const TIME_RANGE_OPTIONS: Array<{ label: string; value: string; minutes: number }> = [
  { label: "15m", value: "15m", minutes: 15 },
  { label: "1h", value: "1h", minutes: 60 },
  { label: "6h", value: "6h", minutes: 360 },
  { label: "24h", value: "24h", minutes: 1440 },
  { label: "7d", value: "7d", minutes: 10080 },
];
